import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { type UserPrefsType, getStoredUserPrefs, setStoredUserPrefs } from '../utils/storage';

const useUserPrefs = (userId?: string | null) => {
  userId = userId || '';

  // Load the initial user prefs (if any) from local storage.
  const [userPrefs, _setUserPrefs] = useState<UserPrefsType>(
    () => getStoredUserPrefs(userId) || {}
  );

  // Whenever we update the user prefs, we also update them in local storage.
  const setUserPrefs = useCallback(
    (userPrefs: Partial<UserPrefsType>) => {
      _setUserPrefs((prev: UserPrefsType) => {
        const nextUserPrefs = { ...prev, ...userPrefs };
        setStoredUserPrefs(userId, nextUserPrefs);
        return nextUserPrefs;
      });
    },
    [userId]
  );

  return useMemo(
    () => ({
      userPrefs,
      setUserPrefs,
    }),
    [userPrefs]
  );
};

type UserPrefsContextValueType = ReturnType<typeof useUserPrefs>;

export const UserPrefsContext = createContext<UserPrefsContextValueType | undefined>(undefined);

type UserPrefsProviderProps = PropsWithChildren & {
  userId?: string | null;
};

export const UserPrefsProvider: FC<UserPrefsProviderProps> = ({ userId, children }) => {
  const value = useUserPrefs(userId);
  return <UserPrefsContext value={value}>{children}</UserPrefsContext>;
};
